import api from './api';

// Artist interface
export interface Artist {
  id: string;
  name: string;
  slug: string;
  bio?: string;
  location?: string;
  genres: string[];
  profileImage?: string;
  coverImage?: string;
  socialLinks?: {
    website?: string;
    instagram?: string;
    twitter?: string;
    facebook?: string;
    soundcloud?: string;
    spotify?: string;
  };
  followers?: number;
  createdAt: string;
  updatedAt: string;
}

// News article interface
export interface NewsArticle {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  coverImage?: string;
  author: {
    id: string;
    name: string;
    profileImage?: string;
  };
  categories: string[];
  tags: string[];
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
}

// Track interface
export interface Track {
  id: string;
  title: string;
  artist: {
    id: string;
    name: string;
  };
  coverImage?: string;
  audioUrl: string;
  duration: number;
  genre?: string;
  releaseDate: string;
  plays: number;
  createdAt: string;
  updatedAt: string;
}

// Collaboration request interface
export interface CollaborationRequest {
  id: string;
  title: string;
  description: string;
  creator: {
    id: string;
    name: string;
    profileImage?: string;
  };
  genres: string[];
  skills: string[];
  location?: string;
  remote: boolean;
  deadline?: string;
  status: 'open' | 'in-progress' | 'completed' | 'closed';
  createdAt: string;
  updatedAt: string;
}

// Home page content interface
export interface HomePageContent {
  trendingArtists: Artist[];
  featuredArtists: Artist[];
  latestNews: NewsArticle[];
  popularTracks: Track[];
  recentCollaborations: CollaborationRequest[];
}

// Home content service
const homeContentService = {
  // Get home page content
  getHomeContent: async (): Promise<HomePageContent> => {
    const response = await api.get<HomePageContent>('home');
    return response.data;
  },
  
  // Get trending artists
  getTrendingArtists: async (limit = 5): Promise<Artist[]> => {
    try {
      const response = await api.get<{ artists: Artist[] }>(`artists/trending?limit=${limit}`);
      return response.data.artists;
    } catch (error) {
      console.error('Error fetching trending artists:', error);
      return [];
    }
  },
  
  // Get featured artists
  getFeaturedArtists: async (limit = 5): Promise<Artist[]> => {
    try {
      const response = await api.get<{ artists: Artist[] }>(`artists/featured?limit=${limit}`);
      return response.data.artists;
    } catch (error) {
      console.error('Error fetching featured artists:', error);
      return [];
    }
  },
  
  // Get latest news
  getLatestNews: async (limit = 5): Promise<NewsArticle[]> => {
    try {
      const response = await api.get<{ articles: NewsArticle[] }>(`news/latest?limit=${limit}`);
      return response.data.articles;
    } catch (error) {
      console.error('Error fetching latest news:', error);
      return [];
    }
  },
  
  // Get popular tracks
  getPopularTracks: async (limit = 5): Promise<Track[]> => {
    try {
      const response = await api.get<{ tracks: Track[] }>(`tracks/popular?limit=${limit}`);
      return response.data.tracks;
    } catch (error) {
      console.error('Error fetching popular tracks:', error);
      return [];
    }
  },
  
  // Get recent collaborations
  getRecentCollaborations: async (limit = 5): Promise<CollaborationRequest[]> => {
    try {
      const response = await api.get<{ collaborations: CollaborationRequest[] }>(`collaborations/recent?limit=${limit}`);
      return response.data.collaborations;
    } catch (error) {
      console.error('Error fetching recent collaborations:', error);
      return [];
    }
  },
  
  // Search artists by query
  searchArtists: async (query: string, limit = 10): Promise<Artist[]> => {
    try {
      const response = await api.get<{ artists: Artist[] }>(`artists/search?q=${encodeURIComponent(query)}&limit=${limit}`);
      return response.data.artists;
    } catch (error) {
      console.error('Error searching artists:', error);
      return [];
    }
  },
  
  // Get artist by ID or slug
  getArtist: async (idOrSlug: string): Promise<Artist | null> => {
    try {
      const response = await api.get<{ artist: Artist }>(`artists/${idOrSlug}`);
      return response.data.artist;
    } catch (error) {
      console.error('Error fetching artist:', error);
      return null;
    }
  },
  
  // Get news article by ID or slug
  getArticle: async (idOrSlug: string): Promise<NewsArticle | null> => {
    try {
      const response = await api.get<{ article: NewsArticle }>(`news/${idOrSlug}`);
      return response.data.article;
    } catch (error) {
      console.error('Error fetching article:', error);
      return null;
    }
  },
};

export default homeContentService; 